import { Link } from "react-router-dom";
import { ArrowRight, UserPlus, Search, MessageCircle, Handshake, CreditCard, Star, Send } from "lucide-react";
import useSEO from "../hooks/useSEO";

const STEPS = [
  {
    n: 1,
    title: "Créez votre compte client",
    text: "Inscrivez-vous gratuitement sur DemkaBiz en quelques secondes.",
    Icon: UserPlus,
  },
  {
    n: 2,
    title: "Trouvez le bon partenaire",
    text: "Un professionnel, une entreprise ou un organisme à qui adresser votre demande.",
    Icon: Search,
  },
  {
    n: 3,
    title: "Contactez directement le prestataire",
    text: "Dans un espace d'échange dédié, sans intermédiaire.",
    Icon: MessageCircle,
  },
  {
    n: 4,
    title: "Échangez et convenez du prix",
    text: "Discutez des modalités de réalisation et des délais (sauf si le service est gratuit).",
    Icon: Handshake,
  },
  {
    n: 5,
    title: "Effectuez le paiement",
    text: "Réglez directement auprès du prestataire, puis recevez votre service.",
    Icon: CreditCard,
  },
  {
    n: 6,
    title: "Évaluez la prestation",
    text: "Attribuez une note en étoiles à la qualité du service rendu.",
    Icon: Star,
  },
];

export default function ServiceHowItWorks() {
  useSEO({
    title: "Je cherche un service — Comment ça marche",
    description: "De la demande à la prestation, DemkaBiz vous met en relation avec le bon partenaire francophone en 6 étapes simples.",
  });
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="how-it-works-page">
      <div className="text-center max-w-3xl mx-auto">
        <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Je cherche un service</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
          Comment ça marche ?
        </h1>
        <p className="text-muted-foreground mt-5 leading-relaxed">
          De votre demande à la prestation, DemkaBiz vous accompagne en <strong className="text-foreground">6 étapes simples</strong>.
          Une plateforme francophone, humaine, sans frais cachés.
        </p>
      </div>

      {/* Visual schema (illustrated) */}
      <div className="mt-12 rounded-3xl overflow-hidden bg-secondary border border-primary/10">
        <img
          src="/media/visuels/schema-comment-ca-fonctionne.jpg"
          alt="Schéma DemkaBiz — De la demande à la prestation en 6 étapes"
          className="w-full h-auto object-contain"
          data-testid="schema-image"
        />
      </div>

      {/* 6 steps grid */}
      <div className="mt-16">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-center">Les 6 étapes détaillées</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {STEPS.map((s) => (
            <div key={s.n} className="card-surface p-6 group hover:shadow-md transition-shadow" data-testid={`step-${s.n}`}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-lg">
                  {s.n}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <s.Icon className="w-4 h-4 text-accent" />
                    <h3 className="font-display font-semibold text-base leading-snug">{s.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA band */}
      <div className="mt-16 rounded-3xl bg-primary text-primary-foreground p-8 sm:p-12 grid lg:grid-cols-[1fr_auto] items-center gap-6" data-testid="cta-band">
        <div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight">Prêt à trouver votre prestataire ?</h2>
          <p className="opacity-90 mt-2 leading-relaxed max-w-2xl">
            Décrivez votre besoin en quelques minutes ou explorez directement notre annuaire de professionnels francophones vérifiés.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <Link
            to="/demande"
            data-testid="cta-request"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent text-white hover:bg-accent/90 transition font-medium"
          >
            <Send className="w-4 h-4" />
            Faire une demande
          </Link>
          <Link
            to="/recherche"
            data-testid="cta-search"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 transition font-medium"
          >
            Explorer l'annuaire <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-8">
        Vous êtes prestataire ? <Link to="/inscription?role=professional" className="text-primary hover:underline">Rejoignez la plateforme</Link>
      </p>
    </div>
  );
}
